import { formatTime, isSameDay } from '@crm/shared';
import { cn } from '@crm/ui';
import { Pencil } from 'lucide-react';
import type { Appointment } from '../api';
import { STATUS_DOT, VIEW_SURFACE } from './calendar-colors';

const dayLabel = new Intl.DateTimeFormat('pt-BR', { month: 'short', weekday: 'short' });

/** Agrupa por dia mantendo a ordem cronologica. */
function groupByDay(appointments: Appointment[]) {
  const groups: { day: Date; items: Appointment[] }[] = [];

  for (const item of [...appointments].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
  )) {
    const day = new Date(item.startAt);
    const last = groups[groups.length - 1];

    if (last && isSameDay(last.day, day)) last.items.push(item);
    else groups.push({ day, items: [item] });
  }

  return groups;
}

/** Visao "Programacao": uma linha por agendamento, agrupada por dia. */
export function ScheduleList({
  appointments,
  onSelect,
}: {
  appointments: Appointment[];
  onSelect: (id: string) => void;
}) {
  const today = new Date();
  const groups = groupByDay(appointments);

  if (groups.length === 0) {
    return (
      <p
        className={cn(
          '-mx-3 border-y border-border py-10 text-center text-sm text-muted-foreground sm:mx-0 sm:rounded-lg sm:border-x',
          VIEW_SURFACE,
        )}
      >
        Nenhum agendamento no periodo.
      </p>
    );
  }

  return (
    <div
      className={cn(
        '-mx-3 divide-y divide-border border-y border-border sm:mx-0 sm:rounded-lg sm:border-x',
        VIEW_SURFACE,
      )}
    >
      {groups.map(({ day, items }) => {
        const isToday = isSameDay(day, today);

        return (
          <div key={day.toISOString()} className="flex gap-3 p-3 sm:gap-4">
            <div className="flex w-16 shrink-0 items-start gap-2 sm:w-24">
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-lg',
                  isToday && 'bg-primary font-medium text-primary-foreground',
                )}
              >
                {day.getDate()}
              </span>
              <span className="pt-2 text-xs uppercase text-muted-foreground">
                {dayLabel.format(day)}
              </span>
            </div>

            <div className="min-w-0 flex-1 space-y-1">
              {items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-sm transition-colors hover:bg-muted"
                >
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[item.status])} />
                  <span className="w-28 shrink-0 text-muted-foreground">
                    {formatTime(item.startAt)} - {formatTime(item.endAt)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{item.customerName}</span>{' '}
                    <span className="text-muted-foreground">{item.serviceSummary}</span>
                  </span>
                  {/* Coluna propria a direita: o lapis fica sempre no mesmo
                      lugar em vez de correr atras do tamanho do texto. */}
                  <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
